import get from 'lodash/get';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { isAgentLogged } from 'yoda-core-components/lib/helpers//Utils/Utils';
import RestClient from '../RestClient/RestClient';
import AccountApiConstants from './AccountApiConstants';
import { RestConfig, getHostConfig } from '../Common/RestConfig';
import { apiHosts } from '../RestClient/FetchHelper.config';
import {
    getOmniWalletCommonConfig,
    getAccountV5Config,
    getOrdersListConfig,
} from './AccountApiHelper';

/**
 * Function to fetch all root level departments.
 */
export const getCartCount = () =>
    RestClient.get(AccountApiConstants.GET_CART_COUNT).then((json) =>
        // console.log(`Parsing departments ${json}`);
        ({ cartCount: json.data.length })
    ); // catch not required already handled in ResClient/FetchHelper.js

/**
 * Function to sign out from the application
 */
export const signOut = () =>
    RestClient.delete(AccountApiConstants.SESSION).then(() => ({
        signedOut: true,
    })); // catch not required already handled in ResClient/FetchHelper.js

/**
 * Function to request OTP for a user account for verification.
 * @param params : Takes phoneNumber
 * @returns {Promise.<T>|*}
 */
export const requestOtp = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.REQUEST_OTP}`;
    config.accountId = params.accountId;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

/**
 * Function to verify OTP for a user account.
 * @param params : Takes phoneNumber, otp
 * @returns {Promise.<T>|*}
 */
export const verifyOtp = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.VERIFY_OTP}`;
    config.accountId = params.accountId;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

/**
 * Function to request OTP to reset password.
 * @param params : Takes phoneNumber
 * @returns {Promise.<T>|*}
 */
export const requestOtpForResetPassword = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.EVENTS}/${AccountApiConstants.REQUEST_OTP}`;
    config.params = params.payload;
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

/**
 * Function to verify OTP to reset password.
 * @param params : Takes phoneNumber, otp
 * @returns {Promise.<T>|*}
 */
export const verifyOtpForResetPassword = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.EVENTS}/${AccountApiConstants.VERIFY_OTP}`;
    config.params = params.payload;
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

/**
 * Function to set new password using phone number
 * @param params : Takes phoneNumber, otp, password
 * @returns {Promise.<T>|*}
 */
export const setNewPasswordUsingPhoneNumber = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.EVENTS}/${AccountApiConstants.SET_NEW_PASSWORD}`;
    config.params = params.payload;
    config.isDPOrder = params.isDPOrder;
    config.guestAccountId = params.guestAccountId;
    config.isXSourceAccountId = params.isXSourceAccountId;
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

/**
 * Function to create user account.
 * @param params : Takes firstName, lastName, emailAddress, password, expressCheckout
 * @returns {Promise.<T>|*}
 */
export const createAccount = (params) => {
    let config = RestConfig.v5AccountConfig();
    config.url = AccountApiConstants.CAM_BASE;
    config.isSourceTokenRequired = true;
    config.userAction = params.userAction;
    config.isMigrationBucket = params.isMigrationBucket;
    config.isClientIdentifier = params.isClientIdentifier;
    config.isDPOrder = true;
    config.emailOptIn = true;
    config.isDPClusterRequired = true;
    delete params.userAction;
    delete params.isMigrationBucket;
    delete params.isClientIdentifier;
    const { customHeaders } = params;
    delete params.customHeaders;
    customHeaders && (config.customHeaders = customHeaders);
    if (params && params.payload && params.payload.isCreateAccountPostOrder) {
        config.isSourceTokenRequired = false;
        config.isCreateAccountPostOrder = params.payload.isCreateAccountPostOrder;
        config.isAuthRequired = true;
        config.params = params.payload.body;
        config.accessToken = params.accessToken;
        config.orderId = params.payload.orderId;
        config.orderSource = params.payload.orderSource;
    } else {
        config.params = params;
    }
    if (params && params.guestAccountId) {
        config.guestAccountId = params.guestAccountId;
        config.isXSourceAccountId = params.guestAccountId;
    }
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

let createGuestAccountPromise = {
    done: true,
};

export const createGuestAccount = (oldAccountId, options) => {
    if (createGuestAccountPromise.done) {
        let config = RestConfig.v5AccountConfig();
        config.url = AccountApiConstants.OAUTH_GUEST_TOKEN_URL;
        config.isGuestUser = true;
        options && options.headers && (config.customHeaders = options.headers);
        options &&
            options.isDPClusterRequired &&
            (config.isDPClusterRequired = options.isDPClusterRequired);
        config = getHostConfig(config);
        if (oldAccountId) {
            config.oldAccountId = oldAccountId;
        }
        createGuestAccountPromise = RestClient.post(config).then((response) => {
            createGuestAccountPromise.done = true;
            return response;
        }); // catch not required already handled in ResClient/FetchHelper.js;
    }

    return createGuestAccountPromise;
};

export const refreshToken = (refreshTokenParam, oldAccountId, customHeaders) => {
    const params = `grant_type=refresh_token&refresh_token=${refreshTokenParam}`;
    let config = RestConfig.v5AccountConfig();
    config.url = AccountApiConstants.OAUTH_TOKEN_URL;
    // /* B2B Voucher Portal code  */
    if (isAgentLogged()) {
        config = RestConfig.v1AccountConfig();
        config.url = AccountApiConstants.B2B_TOKEN_URL;
    }
    /// /////////////////////////////
    config.params = params;
    config.contentType = 'application/x-www-form-urlencoded';
    config.isFormData = true;
    config.isDPOrder = true;
    config.isDPClusterRequired = true;
    config.customHeaders = customHeaders;
    config = getHostConfig(config);
    if (oldAccountId) {
        config.oldAccountId = oldAccountId;
    }
    return RestClient.post(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

export const loginAccount = (params) => {
    // Set the custom headers in the config, and remove from paylod
    const { customHeaders } = params;
    delete params.customHeaders;
    let config = RestConfig.v5AccountConfig();
    config.url = AccountApiConstants.OAUTH_TOKEN_URL;
    config.params = params;
    config.isXOriginHeaderRequired = params.isXOriginHeaderRequired;
    config.isDPOrder = true;
    config.isDPClusterRequired = true;
    config.xOriginValue = params.xOriginValue;
    customHeaders && (config.customHeaders = customHeaders);

    if (params && params.guestAccountId) {
        config.guestAccountId = params.guestAccountId;
        config.isXSourceAccountId = params.guestAccountId;
    }
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 *  Function to delete token from backend on customer logout
 * @param obj : Takes params, customHeaders
 * @returns {Promise.<T>|*}
 */
export const logout = (obj = {}) => {
    // Set the custom headers in the config, and remove from paylod
    const { customHeaders } = obj;
    let config = {
        ...RestConfig.v5AccountConfig(),
        url: AccountApiConstants.OAUTH_LOGOUT_URL,
        params: get(obj, 'params', {}), // as it post so no empty body
        isDPClusterRequired: true,
        ...(customHeaders && { customHeaders }),
    };
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 *  Function to  get the account by access token
 * @param params : Takes accountId, Access_Token
 * @returns {Promise.<T>|*}
 */

export const getAccount = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}`;
    config.accessToken = params.accessToken;

    config = getHostConfig(config);
    return RestClient.get(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 *  Function to  get the session
 */
export const getSession = (params) => {
    const config = {
        url: AccountApiConstants.SESSION,
        contentType: 'application/json',
        acceptVersion: 4,
        version: '/v2',
        host: params.host,
    };
    return RestClient.get(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 * Function to create session.
 * @param params : Takes emailAddress, password, captchaToken
 * @returns {Promise.<T>|*}
 */
export const createSession = (params) => {
    const config = {
        url: AccountApiConstants.SESSION,
        contentType: 'application/json',
        acceptVersion: 4,
        version: '/v2',
        host: params.host,
        isAuthRequired: true,
        accessToken: params.accessToken,
        isAutoLoginReq: true,
    };

    /* istanbul ignore next */
    if (params.postFlag) {
        config.params = {};
        return RestClient.post(config).then((response) => response);
    }
    return RestClient.get(config).then((response) => response);
};

/**
 * Function to create session.
 * @param params : Takes emailAddress, password, captchaToken
 * @returns {Promise.<T>|*}
 */
export const createCheckoutSession = (params) => {
    const config = {
        url: AccountApiConstants.SESSION_CHECKOUT,
        contentType: 'application/json',
        acceptVersion: 4,
        version: '/v2',
        host: params.host,
        params,
    };

    return RestClient.post(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 * Function to delete session.
 * @param params : Takes nothing only cookies
 * @returns {Promise.<T>|*}
 */
export const deleteSession = (params) => {
    const config = {
        url: AccountApiConstants.SESSION,
        contentType: 'application/json',
        acceptVersion: 4,
        version: '/v2',
        host: params.host,
        params,
    };

    return RestClient.delete(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 * Function to reset password.
 * @param params : Takes emailAddress
 * @returns {Promise.<T>|*}
 */
export const resetPassword = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = AccountApiConstants.EVENTS;
    config.isAuthRequired = false;
    config.params = params;
    config.isDPClusterRequired = true;

    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 * Function to set new password.
 * @param params : Takes password
 * @returns {Promise.<T>|*}
 */
export const setPassword = (params, customHeaders) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}`;
    config.accessToken = params.token;
    config.isAuthRequired = false;
    config.isResetAuthRequired = true;
    config.params = params.queryParams;
    config.isDPClusterRequired = true;
    config.customHeaders = customHeaders;
    config.isDPOrder = params.isDPOrder;

    config = getHostConfig(config);
    return RestClient.patch(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 * Function to get email by auth header
 * @param params : Takes accountId and auth token
 * @returns {Promise.<T>|*}
 */
export const getEmailByAuthToken = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}`;
    config.accessToken = params.token;
    config.isAuthRequired = false;
    config.isResetAuthRequired = true;

    config = getHostConfig(config);
    return RestClient.get(config).then((response) => response); // catch not required already handled in ResClient/FetchHelper.js
};

/**
 * Function to get profile.
 * @param params : Takes accountId
 * @returns {Promise.<T>|*}
 */
export const getProfile = (obj) => {
    const { payload: { switchToNewWalletApi, walletExpandParams = [] } = {} } = obj;
    const expandParams = switchToNewWalletApi ? '' : walletExpandParams.join(',');
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}`;
    config.params = {
        expand: `addresses,paymentMethods,preferences,favStore,${expandParams}`,
    };
    config.accessToken = obj.accessToken;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get getAccountProfile.
 * @param params : Takes accountId
 * @returns {Promise.<T>|*}
 */
export const getAccountProfile = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}`;
    config.accessToken = obj.accessToken;
    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get getPLCCBanner.
 * @param params : Takes accountId
 * @returns {Promise.<T>|*}
 */
export const getPLCCBanner = (obj) => {
    const config = getAccountV5Config(obj);
    config.url =
        `${AccountApiConstants.CAM_BASE}/${obj.accountId}/` +
        `${AccountApiConstants.PAYMENT_METHODS}/${AccountApiConstants.BANNER}`;
    config.customHeaders = get(obj, 'payload.customHeaders', {});
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get coupons.
 * @param params : Takes accountId
 * @returns {Promise.<T>|*}
 */
export const getCoupons = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.COUPONS}`;
    config.accessToken = obj.accessToken;
    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

export const getNewWalletDetails = (obj) => {
    const config = getOmniWalletCommonConfig(obj);
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.WALLET}/all`;
    config.accessToken = obj.accessToken;
    const { expand } = get(obj, 'payload', {});
    config.params = {
        ...(expand && { expand }),
    };
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to update profile.
 * @param postData :  accountId accessToken params: array of replace and test information
 * @returns {Promise.<T>|*}
 */
export const updateProfile = (postData) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${postData.accountId}`;
    config.params = postData.payload;
    config.accessToken = postData.accessToken;
    config.isDPClusterRequired = true;

    config = getHostConfig(config);
    return RestClient.patch(config).then((json) => json);
};

export const postSMSEMailPromotions = (postData) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `optin/promotions`;
    config.params = postData.payload;
    config.accessToken = postData.accessToken;
    config.isDPClusterRequired = true;

    config = getHostConfig(config);
    return RestClient.post(config).then((json) => json);
};
/**
 * Function to get addresses.
 * @param accountId : Takes accountId
 * @returns {Promise.<T>|*}
 */
export const getAddresses = (accountId) => {
    const config = {
        url: `${AccountApiConstants.CAM_BASE}/${accountId}/${AccountApiConstants.ADDRESSES}`,
        credentials: 'include',
    };
    return RestClient.get(config).then((response) => response);
};

/**
 * Function to add new address.
 * @param accountParams : Takes accountId
 * @returns {Promise.<T>|*}
 */
export const postCustomerAddress = (accountParams) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${accountParams.accountId}/${AccountApiConstants.ADDRESSES}`;
    config.accessToken = accountParams.accessToken;
    config.isDPClusterRequired = true;
    config.params = accountParams.payload.params;
    config.isXStandardizationRequired = accountParams.payload.isXStandardizationRequired;

    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

/**
 * Function to get addresses.
 * @param accountParams : Takes accountId
 * @returns {Promise.<T>|*}
 */
export const getCustomerAddresses = (accountParams) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${accountParams.accountId}/${AccountApiConstants.ADDRESSES}`;
    if (accountParams.payload && accountParams.payload.notShipToOtherCountry) {
        config.url += '?filter=intl';
    }
    config.accessToken = accountParams.accessToken;

    config = getHostConfig(config);
    return RestClient.get(config).then((response) => response);
};

/**
 * Function to update new address.
 * @param params : Takes accountId
 * @returns {Promise.<T>|*}
 */
export const updateCustomerAddress = (accountParams) => {
    const { accountId } = accountParams;
    const { addressId } = accountParams.payload;

    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${accountId}/${AccountApiConstants.ADDRESSES}/${addressId}`;
    config.accessToken = accountParams.accessToken;
    config.isDPClusterRequired = true;
    config.params = accountParams.payload.params;
    config.isXStandardizationRequired = accountParams.payload.isXStandardizationRequired;
    config = getHostConfig(config);
    return RestClient.put(config).then((response) => response);
};

export const setDefaultAddress = (params) => {
    const payload = params && params.payload ? params.payload : {};
    const addressId = payload.id;
    const url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/addresses/${addressId}`;
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = url;
    config.accessToken = params.accessToken;
    config.params = payload.requestBody;

    config = getHostConfig(config);
    return RestClient.patch(config).then((response) => response);
};

/**
 * Function to delete address from account.
 * @param params : Takes accountId, addressId
 * @returns {Promise.<T>|*}
 */
export const deleteCustomerAddress = (accountParams) => {
    const { accountId } = accountParams;
    const { addressId } = accountParams.payload;
    const { userAction } = accountParams;
    let config = {};
    config = RestConfig.v5AccountConfig();

    config.url = `${AccountApiConstants.CAM_BASE}/${accountId}/${AccountApiConstants.ADDRESSES}/${addressId}`;
    config.accessToken = accountParams.accessToken;
    config.isDPClusterRequired = true;
    config.userAction = userAction;
    config.params = accountParams.payload.params;
    config.isXStandardizationRequired = true;
    if (typeof accountParams.payload.isXStandardizationRequired === 'boolean') {
        config.isXStandardizationRequired = accountParams.payload.isXStandardizationRequired;
    }
    config = getHostConfig(config);
    return RestClient.delete(config).then((json) => json);
};

/**
 * Function to delete address from account as add and create.
 * @param params : Takes accountId, addressId
 * @returns {Promise.<T>|*}
 */
export const deleteAddressWithCreateUpdate = (accountParams) => {
    const { accountId } = accountParams;
    const { addressId } = accountParams.payload;
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url =
        `${AccountApiConstants.CAM_BASE}/${accountId}/${AccountApiConstants.ADDRESSES}` +
        `/${addressId}/${AccountApiConstants.ADDRESSES_UPDATE_CREATE}`;
    config.accessToken = accountParams.accessToken;
    config.isDPClusterRequired = true;
    config.userAction = accountParams.userAction;
    config.params = accountParams.payload.params;
    config.isXStandardizationRequired = accountParams.payload.isXStandardizationRequired;

    config = getHostConfig(config);
    return RestClient.put(config).then((json) => json);
};

/**
 * Function to get payment methods.
 * @param params : accountId
 * @returns {Promise.<T>|*}
 */
export const getPaymentMethods = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.PAYMENT_METHODS}`;
    config.accessToken = obj.accessToken;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get store by id.
 * @param params : accountId
 * @returns {Promise.<T>|*}
 */
export const getStoreById = (obj) => {
    let config = {};
    const { storeId } = obj.payload;
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.STORES}/${storeId}`;
    config.accessToken = obj.accessToken;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get store by id without authentication.
 * @param obj : storeId
 * @returns {Promise.<T>|*}
 */
export const getStoreByIdPublic = (obj) => {
    let config = {};
    const { storeId } = obj.payload;
    config = RestConfig.v1BrowseConfig();
    config.url = `${AccountApiConstants.STORES}/${storeId}`;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get stores.
 * @param params : accountId
 * @returns {Promise.<T>|*}
 */
export const getStores = (obj) => {
    const { accountId, accessToken, payload } = obj;
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${accountId}/${AccountApiConstants.STORES}`;
    config.xClientSource = payload.xClientSource;
    config.accessToken = accessToken;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get stores for logged in user.
 * @param params : accountId
 * @returns {Promise.<T>|*}
 */
export const getStoreForLoggedInUser = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.STORES_DEFAULT}`;
    config.accessToken = obj.accessToken;
    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/** Function to add a store to user profile
 * @param selected store detail eg : {"id": "2555","isDefault": false,"contact": {"firstName": "Alice","lastName": "Sampleton","phone": "8185551234"}}
 * @returns {Promise.<T>|*}
 */
export const addStore = (params) => {
    const url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.STORES}`;
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = url;
    config.isDPClusterRequired = true;
    config.accessToken = params.accessToken;
    config.xClientSource = params.payload.xClientSource;
    config.params = params.payload;

    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

/**
 * Function to delete store from account.
 * @param params : Takes accountId, storeId
 * @returns {Promise.<T>|*}
 */
export const deleteStore = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url =
        `${AccountApiConstants.CAM_BASE}/${params.accountId}/` +
        `${AccountApiConstants.STORES}/${params.storeId}`;
    config.isDPClusterRequired = true;
    config.accessToken = params.accessToken;

    config = getHostConfig(config);
    return RestClient.delete(config).then((json) => json);
};

/** Function to update store
 * @param selected store detail eg : {"isDefault": true}}
 * @returns {Promise.<T>|*}
 */
export const updateStore = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url =
        `${AccountApiConstants.CAM_BASE}/${params.accountId}/` +
        `${AccountApiConstants.STORES}/${params.storeId}`;
    if (!params.storeId && params.payload.storeId) {
        config.url =
            `${AccountApiConstants.CAM_BASE}/${params.accountId}/` +
            `${AccountApiConstants.STORES}/${params.payload.storeId}`;
    }
    config.isDPClusterRequired = true;
    config.accessToken = params.accessToken;
    config.xClientSource = params.payload.xClientSource;
    config.params = params.payload;

    config = getHostConfig(config);
    return RestClient.put(config).then((response) => response);
};
/** Function to get email by token.
 * @param params : token
 * @returns {Promise.<T>|*}
 */
export const getEmailIdByToken = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.EVENTS}/${AccountApiConstants.REWARDS_TOKEN}`;
    config.params = params;

    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};
/** Function to get link reward profile by token.
 * @param params : token, accountID
 * @returns {Promise.<T>|*}
 */
export const getRewardProfile = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.REWARDS_PROFILE}`;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    config.isMigrationBucket =
        params.isMigrationBucket || get(params, 'userAction.isMigrationBucket');
    config.isRewardsAuthLink =
        params.isRewardsAuthLink || get(params, 'userAction.isRewardsAuthLink');
    config.isDPClusterRequired = true;

    config = getHostConfig(config);
    return RestClient.put(config).then((response) => response);
};
/** Function to get email by token.
 * @param params : email base 64
 * @returns {Promise.<T>|*}
 */
export const getAccountByEmail = (params) => {
    let config = {};
    let emailId = params;
    if (params.payload && params.payload.baseEmail) {
        emailId = params.payload.baseEmail;
    }
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.EMAIL_ADDRESSES}/${emailId}/exists`;
    config = getHostConfig(config);
    return RestClient.get(config).then((response) => response);
};

/** Function to review enrollment
 * @param params : accountId, accessToken
 * @returns {Promise.<T>|*}
 */
export const enrollReward = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.REWARDS_PROFILE}`;
    config.accessToken = params.accessToken;
    config.userAction = params.userAction;
    config.params = params.payload;
    config.isDPClusterRequired = true;

    config = getHostConfig(config);
    return RestClient.put(config).then((response) => response);
};

// /* B2B Voucher Portal code  */
export const fetchB2bPayment = (params) => {
    const agencyId = TokenProvider.get('AGENCY_ID') || false;
    let config = {};
    config = RestConfig.v1AccountConfig();
    config.url = `${AccountApiConstants.B2B_PAYMENT_URL}/${agencyId}/${AccountApiConstants.CREDIT_CARD}`;
    config.accessToken = params.accessToken;
    config.params = params.payload;

    config = getHostConfig(config);
    return RestClient.get(config).then((response) => response);
};
export const fetchPaymentMethods = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/payment-methods`;
    config.accessToken = params.accessToken;
    config.params = params.payload;

    config = getHostConfig(config);
    return RestClient.get(config).then((response) => response);
};

export const deletePayment = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/payment-methods/${params.payload.id}`;
    config.accessToken = params.accessToken;
    config.isDPClusterRequired = true;
    config.params = params.payload.id;

    config = getHostConfig(config);
    return RestClient.delete(config).then((response) => response);
};

export const updatePayment = (params) => {
    const cardId = params.payload.cardId || params.payload.id;
    const url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/payment-methods/${cardId}`;
    const config = getAccountV5Config(params);
    config.url = url;
    if (params.payload.recaptchaToken) {
        config.recaptchaToken = params.payload.recaptchaToken;
        delete params.payload.recaptchaToken;
    }
    config.params = params.payload;
    config.userAction = params.userAction;
    config.customHeaders = get(params, 'payload.customHeaders', {});
    config.isDPClusterRequired = true;
    return RestClient.put(config).then((response) => response);
};

export const setDefaultPayment = (params) => {
    const cardId = params.payload.cardId || params.payload.id;
    const url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/payment-methods/${cardId}`;
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = url;
    config.accessToken = params.accessToken;
    config.params = params.payload.requestBody;

    config = getHostConfig(config);
    return RestClient.patch(config).then((response) => response);
};

export const addPayment = (params) => {
    const config = getAccountV5Config(params);
    const url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/payment-methods`;
    config.url = url;
    if (params.payload.recaptchaToken) {
        config.recaptchaToken = params.payload.recaptchaToken;
        delete params.payload.recaptchaToken;
    }
    config.isDPClusterRequired = true;
    config.params = params.payload;
    config.userAction = params.userAction;
    config.customHeaders = get(params, 'payload.customHeaders', {});

    return RestClient.post(config).then((response) => response);
};

export const addAddress = (params) => {
    const url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/addresses`;
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = url;
    config.isDPClusterRequired = true;
    config.accessToken = params.accessToken;
    config.params = params.payload;

    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};
export const checkRegistry = (params) => {
    const context = RestClient.getContext();
    if (!params.itemId || params.itemId === '') {
        return {
            errCode: AccountApiConstants.SKU_ID_ERR_CODE,
            errMessage: AccountApiConstants.SKU_ID_ERR,
        };
    }
    if (params.itemId.length !== 11) {
        return {
            errCode: AccountApiConstants.SKU_ID_LENGTH_ERR_CODE,
            errMessage: AccountApiConstants.SKU_ID_LENGTH_ERR,
        };
    }
    if (!params.product.id || params.product.id === '') {
        return {
            errCode: AccountApiConstants.PRODUCT_ID_ERR_CODE,
            errMessage: AccountApiConstants.PRODUCT_ID_ERR,
        };
    }
    if (!params.quantity) {
        return {
            errCode: AccountApiConstants.QUANTITY_ID_ERR_CODE,
            errMessage: AccountApiConstants.QUANTITY_ID_ERR,
        };
    }

    const config = {
        url: `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.REGISTRY}`,
        credentials: 'include',
        accessToken: params.accessToken,
        contentType: 'application/json',
        isAuthRequired: true,
        host: params.host,
        params: {
            itemId: params.itemId,
            product: params.product,
            quantity: params.quantity,
        },
    };

    if (context && context.deviceType && context.deviceType.isDesktop) {
        config.acceptVersion = 4;
        config.version = '/v2';
    }

    return RestClient.post(config).then((json) => json);
};

export const meevoAppointment = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.MEEVO}`;
    config.accessToken = params.accessToken;
    config.params = params.payload;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

export const getNotifications = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.PREFERENCES}`;
    config.accessToken = obj.accessToken;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

export const updateNotification = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.PREFERENCES}`;
    config.accessToken = obj.accessToken;
    config.params = obj.payload;

    config = getHostConfig(config);
    return RestClient.put(config).then((json) => json);
};

/* OmniWallet Barcode API helpers starts */
/**
 * Function to get eligibility of store if passed store id
 * if not then returns all stores which are eligible for pilot
 * @param {Object} obj : Takes action object
 * @returns {Promise.<T>|*} promiseObject : Promise Object
 */
export const getEligibleStore = (obj) => {
    let config = {};
    const storeId = get(obj, 'payload.id');
    config = RestConfig.v1OmniWalletConfig();
    config.url = `${AccountApiConstants.SETTINGS}/${AccountApiConstants.ELIGIBLESTORES}${
        storeId ? `/${storeId}` : ''
    }`;

    config.isAuthRequired = false;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get barcode snapshot
 * based on expand params
 * @param {Object} obj : Takes action object
 * @returns {Promise.<T>|*} promiseObject : Promise Object
 */
export const getWalletBarcodeSnapshot = (obj) => {
    const config = getOmniWalletCommonConfig(obj);

    config.url =
        `${AccountApiConstants.CAM_BASE}` +
        `/${obj.accountId}/${AccountApiConstants.WALLET}/${AccountApiConstants.BARCODE}`;
    // to get snapshot api based on expand params
    // we would use below parameter to differentiate
    // between refresh polling api vs on-load snapshot call
    const { expand } = obj.payload || {};
    config.params = {
        ...(expand && { expand }),
    };

    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get barcode details
 * for customizing barcode
 * @param obj : Takes action object
 * @returns {Promise.<T>|*} : Promise Object
 */
export const getWalletBarcodeDetails = (obj) => {
    const config = getOmniWalletCommonConfig(obj);
    config.url =
        `${AccountApiConstants.CAM_BASE}` + `/${obj.accountId}/${AccountApiConstants.WALLET}`;

    // to get wallet api based on expand params
    // as requested
    const { expand } = obj.payload || {};
    config.params = {
        ...(expand && { expand }),
    };

    return RestClient.get(config).then((json) => json);
};

/**
 * Function to put barcode selection
 * when changed
 * for customizing barcode
 * @param obj : Takes action object
 * @returns {Promise.<T>|*} : Promise Object
 */
export const putWalletBarcode = (obj) => {
    const config = getOmniWalletCommonConfig(obj);
    config.url =
        `${AccountApiConstants.CAM_BASE}` + `/${obj.accountId}/${AccountApiConstants.WALLET}`;

    config.params = get(obj, 'payload.params', {});
    return RestClient.put(config).then((json) => json);
};

/**
 * Function to update JCP Card
 * mostly in re-auth scenerios
 * @param obj : Takes action object
 * @returns {Promise.<T>|*} : Promise Object
 */
export const updateWalletPayment = (obj) => {
    const cardId = get(obj, 'payload.id', '');
    const url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.TENDERS}/${cardId}`;
    const config = getOmniWalletCommonConfig(obj);
    config.url = url;
    config.params = obj.payload;
    config.userAction = obj.userAction;
    config.isXStandardizationRequired = true;
    if (typeof obj.isXStandardizationRequired === 'boolean') {
        config.isXStandardizationRequired = obj.isXStandardizationRequired;
    }
    config.isDPClusterRequired = true;
    return RestClient.put(config).then((response) => response);
};

/**
 * Function to update JCP Card
 * mostly in re-auth scenerios
 * @param obj : Takes action object
 * @returns {Promise.<T>|*} : Promise Object
 */
export const addWalletPayment = (obj) => {
    const url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.TENDERS}`;
    const config = getOmniWalletCommonConfig(obj);
    config.url = url;
    config.isDPClusterRequired = true;
    config.params = obj.payload;
    config.userAction = obj.userAction;
    config.isXStandardizationRequired = true;

    if (typeof obj.isXStandardizationRequired === 'boolean') {
        config.isXStandardizationRequired = obj.isXStandardizationRequired;
    }
    return RestClient.post(config).then((response) => response);
};

/* OmniWallet Barcode API helpers ends */

export const getOrders = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.ORDER_URL}`;
    config.accessToken = obj.accessToken;

    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

export const getOrdersHistory = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.ORDERS_HISTORY_URL}`;
    config.params = obj.payload;
    config.accessToken = obj.accessToken;
    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

/**
 * Function to get order list from Impulse
 */
export const getOrderList = (obj) => {
    const versionConfig = RestConfig.v1ImpulseConfig();
    const config = getOrdersListConfig(obj, versionConfig);

    return RestClient.get(config).then((json) => json);
};

export const getOrderListFromV3 = (obj) => {
    const versionConfig = RestConfig.v3ImpulseConfig();
    const config = getOrdersListConfig(obj, versionConfig);

    return RestClient.get(config).then((json) => json);
};

export const getOrderById = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url =
        `${AccountApiConstants.CAM_BASE}` +
        `/${obj.accountId}/${AccountApiConstants.ORDER_DETAILS_API_URL}/${obj.payload.token}`;

    if (obj.payload.expand) {
        config.url += `?expand=${obj.payload.expand}`;
    }

    if (obj.payload.xMode) {
        config.xMode = obj.payload.xMode;
    }
    config.accessToken = obj.accessToken;
    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

export const getImpulseOrderById = (obj) => {
    let config = {};
    config = RestConfig.v1ImpulseConfig();
    config.url = `${AccountApiConstants.ORDER_URL}` + `/${obj.payload.token}`;
    config.params = {
        accountId: obj.accountId,
    };
    config.xClientOrigin = 'CAM';
    config.accessToken = obj.accessToken;
    if (get(obj, 'payload.params.phone', false)) {
        config.xPhoneNumber = obj.payload.params.phone;
    }
    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

export const trackOrder = (obj) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${obj.accountId}/${AccountApiConstants.ORDER_URL}/${obj.payload.orderNumber}`;
    if (obj.accessToken) {
        config.accessToken = obj.accessToken;
    }

    if (obj.payload.expand) {
        config.url += `?expand=${obj.payload.expand}`;
    }

    if (obj.payload.xMode) {
        config.xMode = obj.payload.xMode;
    }
    config.xClientOrigin = 'CAM';

    config.params = obj.payload && obj.payload.params ? obj.payload.params : {};
    config = getHostConfig(config);
    return RestClient.post(config).then((json) => json);
};

export const savedItemsMerge = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.SAVED_ITEMS_MERGE}`;
    if (params.accessToken) {
        config.accessToken = params.accessToken;
    }
    config.params = params.data;

    config = getHostConfig(config);
    return RestClient.post(config).then((json) => json);
};

export const getOrderItemsApi = (params) => {
    let config = {};
    config = RestConfig.v5AccountConfig();
    const urlPart1 = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.ORDER_URL}/`;
    const urlPart2 = `${params.payload.orderId}/${AccountApiConstants.GET_ORDER_ITEMS}`;

    config.url = urlPart1 + urlPart2;

    if (params.accessToken) {
        config.accessToken = params.accessToken;
    }

    if (params.payload.phoneNumber) {
        config.isGuestUser = true;
        config.url += `?phoneNumber=${params.payload.phoneNumber}`;
    }
    // config.params = params.data;
    config.apiHost = apiHosts.account;
    config = getHostConfig(config);
    return RestClient.get(config).then((json) => json);
};

export const cancelOrderItemsApi = (params) => {
    // cancel order submit api
    // pointing to account api in phase 1
    // plan to move to impulse api in future

    let config = {};
    config = RestConfig.v5AccountConfig();
    const urlPart1 = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.ORDER_URL}/`;
    const urlPart2 = `${params.payload.orderId}`;

    config.url = urlPart1 + urlPart2;
    if (params.accessToken) {
        config.accessToken = params.accessToken;
    }

    // custom headers for impulse here
    config.action = 'CANCEL';
    config.xClientOrigin = 'CAM';

    config.params = params.payload.data;
    config.apiHost = apiHosts.account;
    config = getHostConfig(config);
    return RestClient.patch(config).then((json) => json);
};

export const updateOrderPayment = (param) => {
    let config = {};
    config = RestConfig.v1ImpulseConfig();
    config.url = `${AccountApiConstants.ORDER_URL}/${param.payload.orderId}/${AccountApiConstants.UPDATE_PAYMENT}`;
    config.accountId = param.accountId;
    config.phoneNumber = param.payload.phoneNumber;
    config.accessToken = param.accessToken;
    config.params = param.payload.data;
    config.phoneNumber ? (config.isGuestUpdatePayment = true) : (config.isAuthUpdatePayment = true);
    config = getHostConfig(config);
    return RestClient.put(config).then((response) => response);
};

export const resolveOrderOnHold = (param) => {
    let config = {};
    config = RestConfig.v1ImpulseConfig();
    config.url = `${AccountApiConstants.ORDER_URL}/${param.payload.orderId}`;
    // impulse specific header
    config.xClientOrigin = 'CAM';
    config.action = 'update_hold';

    if (param.accountId) {
        config.xAccountId = param.accountId;
    }
    if (param.payload.phoneNumber) {
        config.xPhoneNumber = param.payload.phoneNumber;
    }
    config.accessToken = param.accessToken;

    // request body
    config.params = {
        holdTypes: [
            {
                type: 'CANCEL_HOLD',
                status: 'RESOLVED',
            },
        ],
    };
    // config.params = param.payload.data;

    config.apiHost = apiHosts.impulse;
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

export const optin = (optinParams) => {
    let config = RestConfig.v5AccountConfig();
    config.url = AccountApiConstants.OPTIN;
    config.isDPClusterRequired = true;
    config.params = (optinParams || {}).payload ? optinParams.payload : optinParams;
    if (get(optinParams, 'accessToken')) {
        config.accessToken = optinParams.accessToken;
    }
    if (get(config, "params['x-optin-origin']")) {
        config['x-optin-origin'] = config.params['x-optin-origin'];
        delete config.params['x-optin-origin'];
    }
    config = getHostConfig(config);
    return RestClient.post(config).then((response) => response);
};

export const getJcpCardInfoFromProfile = (params) => {
    let config = RestConfig.v5AccountConfig();
    config.url = `${AccountApiConstants.CAM_BASE}/${params.accountId}/${AccountApiConstants.JCP_PAYMENT_METHOD}`;
    config.accessToken = params.accessToken;
    config = getHostConfig(config);
    return RestClient.get(config).then((response) => response);
};

export const getQuickScreenEligibilityCheck = (params) => {
    let config = RestConfig.v1AccountConfig();
    config.url = `${AccountApiConstants.QUICKSCREEN}?accId=${params.accountId}&rewardId=${params.payload.rewardId}`;
    config.accessToken = params.accessToken;
    config = getHostConfig(config);
    return RestClient.get(config).then((response) => response);
};

export const updateQSWidgetDetails = (params) => {
    let config = RestConfig.v1AccountConfig();
    config.url = `${AccountApiConstants.QUICKSCREEN}?accId=${params.accountId}&rewardId=${params.payload.rewardId}`;
    config.accessToken = params.accessToken;
    config.params = params.payload.responsePayload;
    config = getHostConfig(config);
    return RestClient.put(config).then((response) => response);
};
